import * as React from 'react';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet';

import requestUrlInfo from 'js/lib/requestUrlInfo';
import { useLocation } from 'js/lib/useRouter';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import type { MetaNameAndPropertiesProps } from 'bundles/seo/CourseraMetatagsV2Types';
import SeoPathConfigurationQueryWrapper from 'bundles/seo/api/SeoPathConfigurationQueryWrapper';
import { SEO_OVERRIDE_ENABLED_APPS } from 'bundles/seo/common/constants';
import { useSeoPageData } from 'bundles/seo/contexts/SeoPageDataContext';
import {
  generateMetaNameAndProperties,
  generateMetaTitle,
  generateOverrideRuleFromMetaTags,
} from 'bundles/seo/utils/pageMetadata';

declare const COURSERA_APP_NAME: string;

/** Simple Coursera Metatags Component
 * Provides default title, description, and image meta
 * Does not include the override
 * */
const SimpleCourseraMetatagsBaseComponent: React.FC<MetaNameAndPropertiesProps> = (props) => {
  const { title, disableCourseraSuffix } = props;
  const { metaTags: metaTagsFromSeoPageData } = useSeoPageData();

  const overrideRule = useMemo(() => {
    if (metaTagsFromSeoPageData) {
      return generateOverrideRuleFromMetaTags(metaTagsFromSeoPageData);
    }

    return undefined;
  }, [metaTagsFromSeoPageData]);

  const location = useLocation();
  return (
    <Helmet
      title={generateMetaTitle(title, disableCourseraSuffix, overrideRule)}
      meta={generateMetaNameAndProperties(props, location, overrideRule)}
    />
  );
};

const SimpleCourseraMetatags: React.FC<MetaNameAndPropertiesProps> = (props) => {
  const { pathname, search } = useLocation();
  const hostname = requestUrlInfo().hostname;

  // Only fetch page data overrides for apps enabled in the list
  const enabledAppList = SEO_OVERRIDE_ENABLED_APPS;
  const appName = COURSERA_APP_NAME;

  return (
    <SeoPathConfigurationQueryWrapper
      path={hostname + pathname + search}
      shouldSkip={!enabledAppList.includes(appName)}
    >
      <SimpleCourseraMetatagsBaseComponent {...props} />
    </SeoPathConfigurationQueryWrapper>
  );
};

const SimpleCourseraMetatagsWithErrorBoundary: React.FC<MetaNameAndPropertiesProps> = (props) => (
  <ErrorBoundaryWithTagging componentName="seo/SimpleCourseraMetatags">
    <SimpleCourseraMetatags {...props} />
  </ErrorBoundaryWithTagging>
);

export default SimpleCourseraMetatagsWithErrorBoundary;

export const BaseComponent = SimpleCourseraMetatagsBaseComponent;
